<script>
  import { onMount, onDestroy } from 'svelte';
  import { fly, fade, scale } from 'svelte/transition';
  import { createEventDispatcher } from 'svelte';
  import { cubicOut } from 'svelte/easing';

  import RouletteIcon from './icons/RouletteIcon.svelte';
  import StarIcon from './icons/StarIcon.svelte';
  import BoxIcon from './icons/BoxIcon.svelte';
  import CheckIcon from './icons/CheckIcon.svelte';

  export let authData;

  const dispatch = createEventDispatcher();

  let loading = true;
  let error = null;
  let dailyBonusData = null; // { rewards: Array<{day: number, type: string, amount: number | string}>, userState: { currentStreakDay: number, lastClaimedDay: number, canClaimToday: boolean, nextResetTimestamp: number | null } }
  let timeRemaining = '';
  let claimingDay = null;
  let claimedMessage = '';
  let timerInterval = null;

  function formatTime(seconds) {
      if (seconds === null || seconds < 0) return '';
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  function updateTimer() {
      if (!dailyBonusData?.userState?.nextResetTimestamp) {
          timeRemaining = '';
          if (timerInterval) clearInterval(timerInterval);
          timerInterval = null;
          return;
      }
      const now = Date.now();
      const diffSeconds = Math.max(0, Math.floor((dailyBonusData.userState.nextResetTimestamp - now) / 1000));
      timeRemaining = formatTime(diffSeconds);

      if (diffSeconds <= 0) {
          if (timerInterval) clearInterval(timerInterval);
          timerInterval = null;
          fetchDailyBonusData(); 
      }
  }

  async function fetchDailyBonusData() {
    loading = true;
    error = null;
    if (timerInterval) clearInterval(timerInterval); 
    timerInterval = null;

    try {
      const response = await fetch('/api/v1/rewards/daily', { 
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Telegram-Auth': authData
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      dailyBonusData = await response.json();

      if (dailyBonusData?.userState && !dailyBonusData.userState.canClaimToday && dailyBonusData.userState.nextResetTimestamp) {
          updateTimer();
          if (!timerInterval) timerInterval = setInterval(updateTimer, 1000);
      } else {
          timeRemaining = ''; 
      }

    } catch (err) {
      console.error("Failed to load daily bonus data:", err);
      error = "Не удалось загрузить данные ежедневных бонусов.";
      dispatch('error', { message: error });
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    if (authData) {
      fetchDailyBonusData();
    } else {
      error = "Не удалось получить данные аутентификации Telegram.";
      loading = false;
      console.warn("Auth data is missing, cannot fetch daily bonuses.");
    }
  });

  onDestroy(() => {
    if (timerInterval) clearInterval(timerInterval); 
  });

  async function claimReward(day) {
    if (claimingDay || !dailyBonusData?.userState?.canClaimToday || day !== dailyBonusData?.userState?.currentStreakDay) return;

    claimingDay = day;
    claimedMessage = '';
    const reward = dailyBonusData?.rewards?.find(r => r.day === day);
    if (!reward) {
        claimingDay = null;
        return;
    }

    try {
        const response = await fetch('/api/v1/rewards/daily/claim', { 
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Telegram-Auth': authData
            },
            //  body: JSON.stringify({ day: day })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json(); 

        dailyBonusData.userState = result.newState;

        claimedMessage = result.message;
        if (result.addedSpins && result.addedSpins > 0) {
             dispatch('addSpins', result.addedSpins); 
        }

        if (!dailyBonusData.userState.canClaimToday && dailyBonusData.userState.nextResetTimestamp) {
            if (timerInterval) clearInterval(timerInterval);
            updateTimer();
            timerInterval = setInterval(updateTimer, 1000);
        } else {
            timeRemaining = '';
            if (timerInterval) clearInterval(timerInterval);
            timerInterval = null;
        }

        setTimeout(() => claimedMessage = '', 4000);

    } catch (err) {
        console.error("Failed to claim reward:", err);
        claimedMessage = `Ошибка: ${err.message}`;
        dispatch('error', { message: `Не удалось забрать награду: ${err.message}` });
        setTimeout(() => claimedMessage = '', 4000);
    } finally {
        claimingDay = null;
    }
  }

  function getDayState(day) {
      if (!dailyBonusData?.userState) return 'future';
      const { currentStreakDay, lastClaimedDay, canClaimToday } = dailyBonusData.userState;

      if (day < currentStreakDay || (day === currentStreakDay && !canClaimToday)) {
          return 'claimed';
      }
      if (day === currentStreakDay && canClaimToday) {
          return 'available'; 
      }
      return 'future';
  }

  $: day13 = dailyBonusData?.rewards?.find(r => r.day === 13);
  $: day13State = day13 ? getDayState(day13.day) : 'locked';
  $: isClaiming13 = day13 ? claimingDay === day13.day : false;

</script>


<div class="tab-content-container bonus-tab-daily">
  {#if loading}
    <div class="loading-placeholder">
      <div class="spinner large"></div>
      <p>Загружаем ежедневные награды...</p>
    </div>
  {:else if error}
    <div class="error-message" transition:fade>
      <p>{error}</p>
      <button class="retry-button small" on:click={() => window.location.reload()}>Попробовать снова</button>
    </div>
  {:else}
    <div class="daily-bonus-content">
      <div class="bonus-header" transition:fade>
        <h2>Ежедневные награды</h2>
        <p>Забирайте бонусы за ежедневный вход в игру без пропусков. В конце вас ждёт секретный бокс!</p>
      </div>

      <div class="rewards-grid" transition:fade={{ delay: 100 }}>
        {#each dailyBonusData?.rewards?.slice(0, 12) as reward (reward.day)}
          {@const state = getDayState(reward.day)}
          {@const isClaimingThis = claimingDay === reward.day}
          <button
            class="reward-cell state-{state}"
            disabled={state !== 'available' || claimingDay !== null}
            on:click={() => claimReward(reward.day)}
            in:scale={{ delay: 50 + reward.day * 30, duration: 300, start: 0.7, easing: cubicOut }}
          >
            <div class="day-label">День {reward.day}</div>
            <div class="reward-icon">
              {#if reward.type === 'spins'}<RouletteIcon />{:else if reward.type === 'stars'}<StarIcon />{/if}
            </div>
            <div class="reward-amount">+{reward.amount}</div>

            {#if state === 'claimed' || state === 'today-claimed'}
              <div class="claimed-overlay"><CheckIcon /></div>
            {/if}
            {#if state === 'available' && isClaimingThis}
              <div class="claiming-overlay"><div class="spinner tiny"></div></div>
            {/if}
          </button>
        {/each}
      </div>

      {#if day13}
        <button
          class="reward-cell secret-box-cell state-{day13State}"
          disabled={day13State !== 'available' || claimingDay !== null}
          on:click={() => claimReward(day13.day)}
          in:fade={{ delay: 500 }}
        >
          <div class="box-icon-container">
            <BoxIcon />
            {#if day13State === 'available'}
              <div class="pulsing-glow-box"></div>
            {/if}
          </div>
          <div class="box-info">
            <div class="day-label large">День {day13.day}</div>
            <div class="box-title">SECRET BOX</div>
          </div>

          {#if day13State === 'claimed' || day13State === 'today-claimed'}
            <div class="claimed-overlay"><CheckIcon /></div>
          {/if}
          {#if day13State === 'available' && isClaiming13}
            <div class="claiming-overlay"><div class="spinner tiny"></div></div>
          {/if}
        </button>
      {/if}

      {#if claimedMessage}
        <p class="claimed-message bottom" transition:fade={{ duration: 300 }}>{@html claimedMessage}</p>
      {/if}
      {#if !dailyBonusData?.userState?.canClaimToday && timeRemaining}
        <p class="next-bonus-timer" transition:fade>Следующий бонус через: <span>{timeRemaining}</span></p>
      {/if}
    </div>
  {/if}
</div>

<style>
  /* Подключаем общие стили */
  @import './commonTabStyles.css';

  .bonus-tab-daily { 
    justify-content: flex-start;
    height: 100%;
    overflow-y: auto; /* Добавляем возможность скролла */
  }

  .daily-bonus-content {
     width: 100%;
     max-width: 500px; /* Макс ширина */
     display: flex;
     flex-direction: column;
     align-items: center;
     gap: 20px;
     padding-bottom: 20px; /* Отступ снизу для скролла */
     overflow-y: auto; 
     overflow-x: hidden;
  }

  .bonus-header {
     text-align: center;
     width: 100%;
     padding: 0 15px; /* Боковые отступы для лучшей читаемости */
  }
  .bonus-header h2 { margin: 0 0 8px; font-size: 1.6rem; font-weight: 700; color: #E1E9F0; }
  .bonus-header p { color: #A6C5E1; font-size: 0.95rem; line-height: 1.5; margin: 0; max-width: 400px; margin: auto;}

  .rewards-grid {
     display: grid;
     grid-template-columns: repeat(4, 1fr); /* 4 колонки */
     gap: 10px;
     width: 100%;
     overflow-y: auto; 
     overflow-x: hidden;
  }

  .reward-cell {
     background-color: rgba(26, 38, 51, 0.8);
     border: 1px solid rgba(110, 191, 255, 0.1);
     border-radius: 10px;
     padding: 10px 5px;
     display: flex;
     flex-direction: column;
     align-items: center;
     justify-content: center;
     gap: 5px;
     min-height: 90px; /* Минимальная высота ячейки */
     text-align: center;
     position: relative;
     cursor: default;
     overflow-y: auto; 
     overflow-x: hidden;
     transition: background-color 0.3s ease, border-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  }
   .reward-cell:disabled { cursor: default; }

   .day-label { font-size: 0.75rem; color: #8A9FB3; font-weight: 600; }
   .day-label.large { font-size: 1rem; } /* Для бокса */

   .reward-icon {
     margin: 5px 0;
     height: 28px; /* Фикс высота для иконки */
     display: flex;
     align-items: center;
     justify-content: center;
   }
   .reward-icon :global(svg) {
     width: 24px;
     height: 24px;
   }
   .reward-icon :global(svg.roulette-icon) { stroke: #87C3FF; stroke-width: 1.5; }
   .reward-icon :global(svg.star-icon) { fill: #FFA000; width: 26px; height: 26px; }

   .reward-amount {
     font-size: 1rem;
     font-weight: 700;
     color: #D0DCE8;
   }

   /* Состояния ячеек */
   .reward-cell.state-available {
      cursor: pointer;
      border-color: rgba(255, 160, 0, 0.4);
      background-color: rgba(45, 43, 38, 0.8);
      box-shadow: 0 0 15px rgba(255, 160, 0, 0.2);
   }
   .reward-cell.state-available:hover:not(:disabled) {
       transform: translateY(-3px);
       box-shadow: 0 4px 20px rgba(255, 160, 0, 0.3);
       border-color: rgba(255, 160, 0, 0.6);
   }
   .reward-cell.state-claimed, .reward-cell.state-today-claimed {
      opacity: 0.6;
      background-color: rgba(20, 30, 42, 0.6);
   }
   .reward-cell.state-future {
       opacity: 0.4;
       filter: grayscale(50%);
   }

   /* Оверлеи для состояний */
   .claimed-overlay, .claiming-overlay {
       position: absolute;
       inset: 0;
       background-color: rgba(10, 15, 20, 0.6);
       display: flex;
       justify-content: center;
       align-items: center;
       border-radius: 9px; /* Чуть меньше радиуса ячейки */
   }
   .claimed-overlay :global(svg.check-icon) {
       width: 35%;
       height: 35%;
       stroke: #81C784; /* Зеленая галочка */
       stroke-width: 3;
   }
    .claiming-overlay .spinner { border-color: rgba(255, 255, 255, 0.2); border-top-color: #FFF; }
    .claiming-overlay .spinner.tiny { width: 24px; height: 24px; border-width: 3px; }

   /* Секретный Бокс */
   .secret-box-cell {
      grid-column: span 4; /* Занимает всю ширину */
      flex-direction: row;
      padding: 15px;
      gap: 20px;
      align-items: center;
      justify-content: flex-start;
      min-height: 100px;
   }
   .box-icon-container { 
     position: relative;
     display: flex;
     align-items: center;
     justify-content: center;
     width: 50px;
     height: 50px;
   }
   .box-icon-container :global(svg.box-icon) {
      width: 40px;
      height: 40px;
      stroke: #87C3FF; /* Соответствующий другим иконкам цвет */
      stroke-width: 1.5;
   }
   .box-info { 
     text-align: left;
     flex: 1;
   }
   .box-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: #D0DCE8; /* Соответствующий другим наградам цвет */
   }

   /* Пульсация для доступного бокса */
   .pulsing-glow-box {
      position: absolute;
      top: 50%; left: 50%;
      width: 130%; height: 130%; /* Больше, чем иконка */
      background: radial-gradient(circle, rgba(255, 160, 0, 0.3) 0%, rgba(255, 160, 0, 0) 65%);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      animation: pulseGlowBox 2.5s infinite ease-out;
      z-index: -1;
   }
   @keyframes pulseGlowBox {
      0% { transform: translate(-50%, -50%) scale(0.7); opacity: 0.4; }
      70% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.7; }
      100% { transform: translate(-50%, -50%) scale(1.2); opacity: 0; }
   }

   /* Сообщение и таймер снизу */
   .claimed-message.bottom {
       margin-top: 15px;
       font-size: 1rem; font-weight: 600; color: #81C784;
   }
    .next-bonus-timer {
        margin-top: 15px;
        font-size: 0.95rem; color: #A6C5E1;
    }
    .next-bonus-timer span { color: #87C3FF; font-weight: 700; margin-left: 5px; }

</style>